import React, { useEffect, useRef, useState } from "react";
import { DevRequestDetail, DevRequestImpact as Im, DevRequestType as T, DevRequestUpsert } from "../types";
import { devRequestService } from "../services/dataService";
import { DR_IMPACT_LABEL, DR_IMPACTS, DR_TYPE, DR_TYPES, DrPending, drErr, drLayerBlocked, drMakePending, drPastedFiles, drUploadable } from "../utils/devRequests";
import ConfirmModal from "./ConfirmModal";
import { ScreenPickerField } from "./ScreenPicker";
import { DrDropZone } from "./DevRequestFiles";
import "../pages/PurchaseOrders.css";
import "../pages/DevRequests.css";

/**
 * Geliştirme talebi modalı — yeni talep ve düzenleme. Sayfaya gidilmez; liste/çekmece arkada kalır.
 * Yeni talepte akış: talep JSON ile açılır (numara alınır), sonra bekleyen dosyalar tek tek yüklenir.
 * Yükleme kısmen başarısız olursa modal açık kalır: "Yeniden dene" yalnız kalan dosyaları yükler, talep ikinci kez açılmaz.
 */
export interface DevRequestModalProps {
  /** Düzenlenen talep; null = yeni talep. */
  request: DevRequestDetail | null;
  /** Kirli form koruması modalde çözülür, sonra çağrılır. */
  onClose: () => void;
  /** Yeni talepte dosyalar da bittikten (ya da "Dosyasız bitir") sonra; düzenlemede kayıttan sonra (edited: true). */
  onSaved: (r: { id: string; number: string; edited: boolean }) => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
};

// po-* yalnız input.is-invalid tanımlar; açıklama kutusu aynı kırmızı çerçeveyi buradan alır
const INVALID: React.CSSProperties = { borderColor: "var(--po-danger)", boxShadow: "0 0 0 3px rgba(180,35,24,.12)" };
// Üç uzun etki etiketi yarım sütuna sığmaz: segment tam genişlik, düğmeler eşit, gerekirse iki satır
const SEG_FILL: React.CSSProperties = { alignSelf: "stretch", display: "flex" };
const SEG_BTN: React.CSSProperties = { flex: "1 1 0", minWidth: 0, height: "auto", minHeight: "2.5rem", padding: "0.35rem 0.5rem", lineHeight: 1.25, letterSpacing: 0 };

const Field: React.FC<{ label: string; hint?: string; as?: "label" | "div"; labelId?: string; children: React.ReactNode }> = ({ label, hint, as = "label", labelId, children }) => {
  const Tag = as as any;
  return <Tag className="po-field"><span id={labelId}>{label}{hint && <em>{hint}</em>}</span>{children}</Tag>;
};

const DevRequestModal: React.FC<DevRequestModalProps> = ({ request, onClose, onSaved }) => {
  // ---- Form durumu (ilk değerler: talep ya da boş form; tür seçili gelmez) ----
  const [type, setType] = useState<T | 0>(request?.type ?? 0);
  const [title, setTitle] = useState(request?.title ?? "");
  const [description, setDescription] = useState(request?.description ?? "");
  const [screenCode, setScreenCode] = useState<string | null>(request?.screenCode ?? null);
  const [screenName, setScreenName] = useState<string | null>(request?.screenName ?? null);
  const [impact, setImpact] = useState<Im>(request?.impact ?? Im.Medium);
  const [files, setFiles] = useState<DrPending[]>([]);

  const [tried, setTried] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<null | "save" | "upload">(null);
  const [step, setStep] = useState(""); // "Dosyalar yükleniyor 2/3…"
  const [created, setCreated] = useState<{ id: string; number: string } | null>(null);
  const [guardOpen, setGuardOpen] = useState(false);

  const createdRef = useRef<{ id: string; number: string } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const typeRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const titleRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  const filesRef = useRef<HTMLDivElement>(null);
  const alive = useRef(true);

  const snapshot = JSON.stringify({ type, title, description, screenCode, impact, files: files.map((p) => p.key) });
  const [initialJson] = useState(snapshot);
  const locked = !!created; // talep açıldıktan sonra alanlar salt okunur; yalnız dosyalar yeniden denenir
  const uploadable = files.filter(drUploadable);
  const failed = uploadable.filter((p) => !!p.error);
  const okCount = files.filter((p) => !p.rejected).length;
  const dirty = created ? uploadable.length > 0 : snapshot !== initialJson;
  const info = DR_TYPE[type || T.Bug];

  const tl = title.trim().length;
  const typeErr = !type ? "Talep türünü seçin." : "";
  const titleErr = tl < 5 || tl > 150 ? "Başlık en az 5, en fazla 150 karakter olmalı." : "";
  const descErr = description.trim().length < 10 ? "Açıklama en az 10 karakter olmalı." : "";

  // Açan öğe ilk render'da (odak modala geçmeden) yakalanır; kapanışta odak ona döner
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // autoFocus yerine efekt: StrictMode geliştirmede geri-odak temizliğini bir kez erken çalıştırır, autoFocus ezilirdi
  useEffect(() => {
    typeRefs.current[Math.max(0, DR_TYPES.indexOf(type as T))]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kaydedilmemiş değişiklik varken sekme kapatma/yenileme uyarısı
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const pickType = (t: T) => { if (locked) return; setType(t); setError(""); };
  // Tür kartları radyo grubu: oklar seçimi taşır (odak da birlikte)
  const typeKey = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const n = (i + d + DR_TYPES.length) % DR_TYPES.length;
    pickType(DR_TYPES[n]);
    typeRefs.current[n]?.focus();
  };

  /** Ctrl+V: panodaki görseller/dosyalar bekleyen eklere girer. Metin kutusunda metinle birlikte gelen görsel (Excel/Word) metin olarak yapışır. */
  const onPaste = (e: React.ClipboardEvent) => {
    if (request) return;
    const t = e.target as HTMLElement;
    const typing = t.tagName === "TEXTAREA" || t.tagName === "INPUT";
    if (typing && e.clipboardData?.getData("text/plain")) return;
    const got = drPastedFiles(e.clipboardData);
    if (!got.length) return;
    e.preventDefault();
    if (busy) return;
    setFiles((f) => drMakePending(got, f));
    setError("");
    window.setTimeout(() => filesRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 0);
  };

  /** Bırakma alanı dışına bırakılan dosya tarayıcıda açılıp uygulamadan çıkarmasın: yeni talepte eklere girer, düzenlemede yok sayılır. */
  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).indexOf("Files") >= 0;
  const onDragOver = (e: React.DragEvent) => {
    if (!hasFiles(e) || e.nativeEvent.defaultPrevented) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = request || busy ? "none" : "copy";
  };
  const onDrop = (e: React.DragEvent) => {
    if (!hasFiles(e) || e.nativeEvent.defaultPrevented) return;   // bırakma alanı kendisi işledi
    e.preventDefault();
    if (request || busy) return;
    const got = Array.from(e.dataTransfer.files);
    if (!got.length) return;
    setFiles((f) => drMakePending(got, f));
    setError("");
    window.setTimeout(() => filesRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 0);
  };

  // ---- Doğrulama ve kayıt ----
  const showError = (msg: string) => { setError(msg); bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" }); };
  const validate = (): boolean => {
    setTried(true);
    const bad = typeErr ? typeRefs.current[0] : titleErr ? titleRef.current : descErr ? descRef.current : null;
    if (bad) { window.setTimeout(() => bad.focus(), 0); return false; }
    return true;
  };
  const dto = (): DevRequestUpsert => ({ type: type as T, title: title.trim(), description: description.trim(), screenCode, impact });

  const patch = (key: string, p: Partial<DrPending>) => setFiles((list) => list.map((x) => (x.key === key ? { ...x, ...p } : x)));
  /** Dosyalar sırayla, tek tek (dosya başına ilerleme ve yeniden deneme). Başarısız sayısını döner. */
  const uploadAll = async (id: string, queue: DrPending[]) => {
    let bad = 0;
    for (let i = 0; i < queue.length; i++) {
      if (!alive.current) break;
      const p = queue[i];
      setStep(`Dosyalar yükleniyor ${i + 1}/${queue.length}…`);
      patch(p.key, { progress: 0, error: undefined });
      try {
        // %100'ü sunucu kaydı bitince yaz: gönderim bitse de içerik denetimi reddedebilir
        await devRequestService.upload(id, p.file, (pct) => patch(p.key, { progress: Math.min(99, pct) }));
        patch(p.key, { progress: 100 });
      } catch (e) {
        bad++;
        patch(p.key, { progress: undefined, error: drErr(e, "Dosya yüklenemedi.") });
      }
    }
    return bad;
  };

  const submit = async () => {
    if (busy) return;
    setError("");
    if (request) {
      if (!dirty) { onClose(); return; }
      if (!validate()) return;
      setBusy("save");
      try { await devRequestService.update(request.devRequestId, dto()); }
      catch (e) { if (alive.current) { setBusy(null); showError(drErr(e, "Talep kaydedilemedi.")); } return; }
      onSaved({ id: request.devRequestId, number: request.number, edited: true });
      return;
    }
    let made = createdRef.current;
    if (!made) {
      if (!validate()) return;
      setBusy("save");
      try {
        const r = await devRequestService.create(dto());
        made = { id: r.devRequestId, number: r.number };
        createdRef.current = made; // yeniden denemede ikinci talep açılmasın
        setCreated(made);
      } catch (e) { if (alive.current) { setBusy(null); showError(drErr(e, "Talep gönderilemedi.")); } return; }
    }
    const queue = files.filter(drUploadable);
    let bad = 0;
    if (queue.length) { setBusy("upload"); bad = await uploadAll(made.id, queue); }
    if (!alive.current) return;
    setBusy(null); setStep("");
    if (bad === 0) onSaved({ id: made.id, number: made.number, edited: false });
    else bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finish = () => (createdRef.current ? onSaved({ ...createdRef.current, edited: false }) : onClose());
  const requestClose = () => { if (busy) return; if (dirty) setGuardOpen(true); else finish(); };

  // ---- Modal kısayolları: Esc kapat · Ctrl+Enter gönder ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Üstte ekran seçici ya da onay varsa Esc onlarındır: burada stopPropagation YOK, yoksa seçici Esc'i hiç görmez
      if (guardOpen || drLayerBlocked(overlayRef.current)) return;
      // stopPropagation şart: arkadaki çekmece ve aynı basımda açılan kirli-form onayı Esc'i ikinci kez almasın
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); requestClose(); }
      else if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); submit(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  });

  const retry = !!created && !busy;
  const primaryText = busy === "save" ? (request ? "Kaydediliyor…" : "Gönderiliyor…")
    : busy === "upload" ? step
      : retry ? (uploadable.length ? "Yeniden dene" : "Bitir")
        : request ? "Kaydet" : "Talebi gönder";

  return (
    <div className="po-overlay center" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }} onDragOver={onDragOver} onDrop={onDrop}>
      <form className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="dr-om-title" noValidate onSubmit={(e) => e.preventDefault()} onPaste={onPaste}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">{request ? `${request.number} · Düzenle` : "Yeni talep"}</span>
            <h3 id="dr-om-title">{request ? "Talebi düzenle" : "Talep bildir"}</h3>
          </div>
          <div className="po-modal-head-side">
            {dirty && <span className="po-dirty">Kaydedilmemiş değişiklik</span>}
            <button type="button" className="po-ib big" aria-label="Kapat" onClick={requestClose} disabled={!!busy}>{I.close}</button>
          </div>
        </div>

        <div className="po-modal-body" ref={bodyRef}>
          {retry && failed.length > 0 && <div className="po-note warn" role="alert">{created!.number} oluşturuldu; {failed.length} dosya yüklenemedi.</div>}
          {error && <div className="po-note err" role="alert">{error}</div>}

          {/* 1 — Tür (varsayılan yok) */}
          <div className="po-field">
            <span>Talep türü</span>
            <div className={`dr-typecards ${tried && typeErr ? "is-invalid" : ""}`} role="radiogroup" aria-label="Talep türü" aria-invalid={tried && !!typeErr}>
              {DR_TYPES.map((t, i) => {
                const ti = DR_TYPE[t];
                const on = type === t;
                return (
                  <button type="button" key={t} ref={(el) => { typeRefs.current[i] = el; }} role="radio" aria-checked={on}
                    tabIndex={on || (!type && i === 0) ? 0 : -1} className={`dr-typecard ${on ? "on" : ""}`} disabled={locked}
                    onClick={() => pickType(t)} onKeyDown={(e) => typeKey(e, i)}>
                    <span className="po-mark" style={{ ["--po-hue" as any]: ti.hue }} aria-hidden="true">{ti.glyph}</span>
                    <b>{ti.label}</b><small>{ti.hint}</small>
                  </button>
                );
              })}
            </div>
            {tried && typeErr && <span className="po-fielderr">{typeErr}</span>}
          </div>

          {/* 2 — Başlık */}
          <Field label="Başlık" hint="5–150 karakter">
            <input ref={titleRef} value={title} maxLength={150} readOnly={locked} placeholder={info.titlePh}
              className={tried && titleErr ? "is-invalid" : ""} aria-invalid={tried && !!titleErr}
              onChange={(e) => { setTitle(e.target.value); setError(""); }} />
            <span className="po-counter">{title.length}/150</span>
            {tried && titleErr && <span className="po-fielderr">{titleErr}</span>}
          </Field>

          {/* 3 — Ekran + etki */}
          <div className="po-grid2">
            <Field label="Ekran" hint="isteğe bağlı" as="div" labelId="dr-om-screen">
              <ScreenPickerField value={screenCode} screenName={screenName} nullLabel="Genel / emin değilim" disabled={locked} labelledBy="dr-om-screen"
                onChange={(code, name) => { setScreenCode(code); setScreenName(name); setError(""); }} />
            </Field>
            <Field label="Etkisi" as="div">
              <div className="po-seg" role="radiogroup" aria-label="Etkisi" style={SEG_FILL}>
                {DR_IMPACTS.map((v) => (
                  <button type="button" key={v} role="radio" aria-checked={impact === v} className={impact === v ? "on" : ""} style={SEG_BTN} disabled={locked}
                    onClick={() => { setImpact(v); setError(""); }}>{DR_IMPACT_LABEL[v]}</button>
                ))}
              </div>
              <span className={`po-fieldnote ${impact === Im.Blocking ? "warn" : ""}`}>Yalnız işiniz tamamen duruyorsa seçin; talepleri yönetenlere hemen bildirilir.</span>
            </Field>
          </div>

          {/* 4 — Açıklama (düz metin) */}
          <Field label="Açıklama" hint="en az 10 karakter">
            <textarea ref={descRef} rows={8} maxLength={4000} value={description} readOnly={locked} placeholder={info.descPh}
              className={tried && descErr ? "is-invalid" : ""} style={tried && descErr ? INVALID : undefined} aria-invalid={tried && !!descErr}
              onChange={(e) => { setDescription(e.target.value); setError(""); }} />
            <span className="po-counter">{description.length}/4000</span>
            {tried && descErr && <span className="po-fielderr">{descErr}</span>}
          </Field>

          {/* 5 — Ekler (yalnız yeni talepte; düzenlemede dosyalar çekmeceden eklenir) */}
          {!request && (
            <div className="po-field" ref={filesRef}>
              <span>Ekler{okCount > 0 ? <em>{okCount} dosya</em> : <em>isteğe bağlı</em>}</span>
              <DrDropZone files={files} onChange={(f) => { setFiles(f); setError(""); }} disabled={!!busy} />
            </div>
          )}
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint">{request ? "Değişiklikler talebin yazışma ve geçmiş akışında görünür." : "Gönderince numara verilir; durum değişince ve yanıt gelince bildirim alırsınız."}</span>
          <div className="po-foot-btns">
            {retry ? (
              uploadable.length > 0 && <button type="button" className="po-btn" onClick={finish}>Dosyasız bitir</button>
            ) : (
              <button type="button" className="po-btn" onClick={requestClose} disabled={!!busy}>Vazgeç</button>
            )}
            <button type="button" className="po-btn primary" onClick={submit} disabled={!!busy}>{!busy && !retry && I.send} {primaryText}</button>
          </div>
        </div>
      </form>

      <ConfirmModal isOpen={guardOpen} type="warning" title="Kaydedilmemiş değişiklikler" message="Bu talepteki bilgiler kaydedilmedi. Kapatırsanız kaybolur."
        confirmText="Kapat" cancelText="Düzenlemeye dön" onConfirm={() => { setGuardOpen(false); finish(); }} onCancel={() => setGuardOpen(false)} />
    </div>
  );
};

export default DevRequestModal;
